import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { isNil, isNumber } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { CareOverviewInsights } from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

interface CareInsightsOverviewProps {
  careOverviewInsightsForSelectedMonthYear?: CareOverviewInsights
  isLoadingProviderInsights: boolean
  emptyClinicalQualitySectionCount: number
  totalClinicalQualitySections: number
  onLearnMorePress: () => void
}

const CareInsightsOverviewContainer = styled.View(({ theme }) => ({
  marginBottom: theme.spacing['24px'],
}))

const MetricsContainer = styled.View(({ theme }) => ({
  marginTop: theme.spacing['8px'],
  gap: theme.spacing['16px'],
}))

const MetricsRow = styled.View(({ theme }) => ({
  flexDirection: theme.breakpoints.isMinWidthTablet ? 'row' : 'column',
  gap: theme.spacing['16px'],
}))

const HighLevelMetricContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['12px'],
  padding: theme.spacing['16px'],
  borderRadius: '8px',
  backgroundColor: theme.colors.backgroundElement,
  flexBasis: '100%',
  flexShrink: 1,
}))

const CareInsightDisclaimerText = styled(BodyText)(({ theme }) => ({
  marginTop: theme.spacing['4px'],
}))

const HighLevelMetric: React.FC<{
  value: string | number
  label: string
  testIDPrefix: string
}> = ({ value, label, testIDPrefix }) => {
  return (
    <HighLevelMetricContainer testID={tID(`${testIDPrefix}-container`)}>
      <Title size={TitleSize.LARGE} text={value} testID={tID(`${testIDPrefix}-value`)} />
      <BodyText text={label} testID={tID(`${testIDPrefix}-label`)} />
    </HighLevelMetricContainer>
  )
}

export const CareInsightsOverview: React.FC<CareInsightsOverviewProps> = ({
  careOverviewInsightsForSelectedMonthYear,
  isLoadingProviderInsights,
  emptyClinicalQualitySectionCount,
  totalClinicalQualitySections,
  onLearnMorePress,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  const {
    total_sessions_count: totalSessionsCount,
    total_sessions_clients_count: totalSessionsClientsCount,
    care_start_clients: careStartClients,
    care_end_clients: careEndClients,
    moderate_severe_clients_success_rate: moderateSevereClientsSuccessRate,
    mild_moderate_clients_success_rate: mildModerateClientsSuccessRate,
  } = careOverviewInsightsForSelectedMonthYear ?? {}

  return (
    <CareInsightsOverviewContainer>
      {!isNil(totalSessionsCount) && !isNil(totalSessionsClientsCount) && (
        <BodyText
          text={formatMessage(
            {
              defaultMessage:
                "Throughout this calendar month, you've completed {totalSessionsCount} attended sessions across {totalSessionsClientsCount} clients. Let's break that down:",
              description:
                'Description of number of sessions completed and number of clients served in the selected month for the provider',
            },
            {
              totalSessionsCount: isNumber(totalSessionsCount) ? totalSessionsCount : 'N/A',
              totalSessionsClientsCount: isNumber(totalSessionsClientsCount) ? totalSessionsClientsCount : 'N/A',
            },
          )}
          testID={tID('CareInsights-sessionQuantityDescriptionText')}
        />
      )}
      <MetricsContainer>
        <MetricsRow>
          <HighLevelMetric
            value={isNumber(careStartClients) ? careStartClients : 'N/A'}
            label={formatMessage({
              defaultMessage: 'Clients that started care',
              description: 'Number of clients that started care in the selected month for the provider',
            })}
            testIDPrefix={'CareInsightsOverview-clientsStartedCare'}
          />
          <HighLevelMetric
            value={isNumber(careEndClients) ? careEndClients : 'N/A'}
            label={formatMessage({
              defaultMessage: 'Clients graduated',
              description: 'Number of clients that graduated in the selected month for the provider',
            })}
            testIDPrefix={'CareInsightsOverview-clientsGraduated'}
          />
        </MetricsRow>
        <MetricsRow>
          <HighLevelMetric
            value={
              isNumber(moderateSevereClientsSuccessRate)
                ? Math.round(moderateSevereClientsSuccessRate * 100) + '%'
                : 'N/A'
            }
            label={formatMessage({
              defaultMessage: 'Success rate with moderate/severe clients',
              description: 'Success rate with moderate/severe clients in the selected month for the provider',
            })}
            testIDPrefix={'CareInsightsOverview-successRateModerateSevereClients'}
          />
          <HighLevelMetric
            value={
              isNumber(mildModerateClientsSuccessRate) ? Math.round(mildModerateClientsSuccessRate * 100) + '%' : 'N/A'
            }
            label={formatMessage({
              defaultMessage: 'Success rate with mild/minimal clients',
              description: 'Success rate with mild/minimal clients in the selected month for the provider',
            })}
            testIDPrefix={'CareInsightsOverview-successRateMildMinimalClients'}
          />
        </MetricsRow>
      </MetricsContainer>
      {!isLoadingProviderInsights && emptyClinicalQualitySectionCount < totalClinicalQualitySections && (
        <CareInsightDisclaimerText
          testID={tID('CareInsightsOverview-disclaimerText')}
          size={BodyTextSize.LARGE}
          color={colors.textSecondary}
          text={
            <FormattedMessage
              defaultMessage='Care is not one-size-fits-all. These AI-generated insights below are meant as a tool for self-reflection and the development of your expertise. <link>Learn more</link>'
              description='Text under care insights overview indicating that the AI generated insights are to be used at the provider’s discretion'
              values={{
                link: (chunks: string) => (
                  <Link
                    text={chunks}
                    testID={tID('CareInsightsOverview-disclaimerText-learnMoreLink')}
                    size={BodyTextSize.LARGE}
                    onPress={() => {
                      onLearnMorePress()
                    }}
                    underline
                  />
                ),
              }}
            />
          }
        />
      )}
    </CareInsightsOverviewContainer>
  )
}

export default CareInsightsOverview
